<template>
  <div>
    <div class="mt-4">
      <AlertBannerRecords path="/vehicles" :message="vehiclesMessage" />
    </div>

    <!-- Search -->
    <div class="row mt-4">
      <div class="col-md-8">
        <input
          class="input form-control"
          @keypress.enter="find()"
          v-model="query.generic"
          placeholder="Pesquisar por Placa, AIT ou Renavam"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <button
        class="col-md-2 ml-2 btn-sm btn-outline"
        :class="{ 'btn-outline-active': showFilter }"
        @click="showFilter = !showFilter"
      >
        Filtros
        <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': showFilter }"/>
      </button>

      <button v-if="changeQuery" @click="find()" class="btn btn-link">
        Aplicar filtros ({{ numberOfBadges }})
      </button>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters
          :query="query"
          @clearBadge="find()"
          @clearAllBadges="find()"
          :amount-changed="updateNumberOfBadges"
          :filters="badgesFilters"
        />
      </div>
    </div>

    <!-- Filters -->
    <template v-if="showFilter">
      <div class="row mt-6">
        <div class="col-md-6">
          <label>Empresas</label>
          <Select2 :options="filterCnpjs" v-model="query.cnpj"/>
        </div>

        <div class="col-md-6">
          <label>Órgão autuador</label>
          <Select2 :options="filterIssuingAuthority" v-model="query.issuingAuthority"/>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-6">
          <label>Status</label>
          <Select2 :options="filterStatus" v-model="query.driverIndicationStatusId"/>
        </div>

        <div class="col-md-6">
          <label>Classe</label>
          <Select2 :options="filterClass" v-model="query.ticketClass"/>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-6">
          <label>Protocolo de encaminhamento para indicação</label>
          <input
            class="form-control"
            v-model="query.indicationProtocol"
            placeholder="Digite o protocolo (ex: IND66673001)"
          />
        </div>

        <div class="col-md-6">
          <label>Motorista</label>
          <Select2 :options="filterDrivers" v-model="query.cpf"/>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="occurred_at">Data da infração</option>
            <option value="created_at">Data de criação</option>
            <option value="indication_deadline">Prazo de indicação</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>
    </template>

    <!-- Actions -->
    <div class="row mt-4">
      <div class="col-md-12">
        <Dropdown
          title-dropdown="Exportar"
          class="btn btn-primary mt-1 mr-1"
          :enable="someTicketSelected"
        >
          <ul class="mb-1 mt-1">
            <li class="dropdown-item" @click="openReportConfirm()">
              Relatório Indicação de Condutores
            </li>
          </ul>
        </Dropdown>

        <button
          class="btn btn-outline mt-1 float-right relative"
          @click="showSelectColumns = true"
        >
          Mostrar colunas
        </button>
      </div>
    </div>

    <!-- Info -->
    <div class="row mt-4">
      <div class="col-md-12">
        <small>
          {{ infoTotalModels }}
          <template v-if="meta.lastConsult">
            Última consulta realizada em: {{ meta.lastConsult | datetime }}
          </template>
        </small>

        <button
          class="btn btn-primary ml-3 mb-1"
          v-if="selectedTickets.length > 0"
          :disabled="indicationButtonDisabled"
          @click="indicateDriver"
        >
          {{ indicationButtonText }}
        </button>

        <button
          class="btn btn-outline ml-3 mb-1"
          v-if="selectedTickets.length > 0 && canDeclineIndication"
          :disabled="accountIsClosed || declinableTickets.length === 0"
          @click="showDeclineModal = true"
        >
          Não quero indicar ({{ declinableTickets.length }})
        </button>

        <button
          class="btn btn-outline ml-3 mb-1"
          v-if="tickets.length > 0"
          @click="handleSelectAll"
          :class="{ 'btn-outline-active': checkAll }"
        >
          {{ !checkAll ? 'Selecionar todas' : 'Desmarcar todas' }} ({{ meta.totalCount || 'Calculando' }})
        </button>

        <div class="float-right" style="display: inline-flex;align-items: center;">
          <small class="mr-2" style="white-space: nowrap">Ordenar por:</small>
          <select v-model="order" @change="updateOrder(order)" class="browser-default custom-select">
            <option :value="['updated_at', 'desc']">Data de atualização</option>
            <option :value="['occurred_at', 'desc']">Data da infração</option>
            <option :value="['created_at', 'desc']">Data de criação</option>
            <option :value="['indication_deadline', 'asc']">Prazo de indicação</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row flex-fill d-flex justify-content-start">
      <div class="col-md-12 mt-2">
        <DriverIndicationTable
          :tickets="tickets"
          :columns="selectedColumns"
          :getStatusName="getStatusName"
          @changeCheckAllRow="changeCheckAllRow"
          @changeCheckRow="changeCheckRow"
          :check-all-row="checkAllRow"
        />

        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Batch Review Modal -->
    <BatchReviewModal
      v-model="showBatchReviewModal"
      :selected-tickets="selectedTickets"
      @confirmBatch="processBatchIndication"
    />

    <DeclineIndicationModal
      v-model="showDeclineModal"
      :indications="declinableTickets"
      :ignored="declineIgnoredTickets"
      @finished="handleDeclineFinished"
    />

    <!-- Driver Selection Modal -->
    <DriverSelectionModal
      ref="driverSelectionModal"
      v-model="showDriverModal"
      :selected-tickets="currentBatchTickets"
      @confirmDriver="handleDriverSelection"
      @openDriverForm="openDriverForm"
    />

    <!-- Single Driver Batch Confirmation Modal -->
    <SingleDriverBatchModal
      v-if="singleDriverBatchData.driver"
      v-model="showSingleDriverBatchModal"
      :driver="singleDriverBatchData.driver"
      :tickets="singleDriverBatchData.tickets"
      @confirm="confirmSingleDriverBatch"
      @cancel="cancelSingleDriverBatch"
    />

    <!-- Driver Confirmation Modal -->
    <Modal v-model="showDriverConfirmationModal" width="md">
      <span slot="header">Confirmar Indicação de Condutor</span>
      <div v-if="selectedDriver">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <strong>Você tem certeza que deseja indicar o motorista:</strong>
          <span v-if="batchGroups.length > 0" class="badge badge-info">
            Selecionado Motorista {{ currentBatchIndex + 1 }} de {{ batchGroups.length }}
          </span>
        </div>

        <!-- Bloco de informações do motorista -->
        <div class="mb-3 p-3 info-box">
          <p class="mb-1 info-line">
            <strong>Nome:</strong> {{ selectedDriver.name }}
          </p>
          <p class="mb-1 info-line">
            <strong>CPF:</strong> {{ selectedDriver.cpf | cpf }}
          </p>
          <p class="mb-0 info-line">
            <strong>Categoria:</strong> {{ selectedDriver.category }}
          </p>
        </div>

        <!-- Bloco de multas selecionadas -->
        <div class="mb-3 p-3 info-box">
          <p class="mb-2 info-title">
            <strong>Multas selecionadas ({{ ticketsForConfirmation.length }}):</strong>
          </p>
          <div class="tickets-scroll">
            <small v-for="indication in ticketsForConfirmation" :key="indication.id" class="d-block mb-1 ticket-detail">
              • AIT: <strong>{{ (indication.ticket && indication.ticket.ait) || indication.ait }}</strong> - Placa: <strong>{{ (indication.ticket && indication.ticket.plate) || indication.plate }}</strong>
            </small>
          </div>
        </div>

        <!-- Phone Confirmation Section -->
        <div class="alert alert-warning mt-4">
          <p class="mb-2"><strong>ATENÇÃO: Verificação de Telefone</strong></p>

          <p class="mb-3">
            <strong>É FUNDAMENTAL que o telefone esteja correto!</strong><br>
            Se o número cadastrado estiver incorreto, <strong>NÃO conseguiremos entrar em contato com o responsável</strong> para dar seguimento no processo de indicação.
          </p>

          <div v-if="!phoneConfirmed" class="mb-2">
            <p class="mb-2">
              O telefone <strong>{{ confirmedPhone || '(não informado)' }}</strong> está correto?
            </p>
            <input
              id="confirm-phone"
              v-model="confirmedPhone"
              type="text"
              class="form-control mb-3"
              placeholder="(00) 00000-0000"
              maxlength="15"
              :readonly="!phoneEditing"
              @input="formatConfirmPhone"
            >

            <div class="d-flex">
              <button
                v-if="!phoneEditing"
                @click="enablePhoneEditing"
                class="btn btn-outline mr-2"
                style="flex: 1;"
              >
                Não, preciso editar
              </button>
              <button
                v-if="phoneEditing"
                @click="savePhoneEdit"
                class="btn btn-primary mr-2"
                style="flex: 1;"
                :disabled="!confirmedPhone || confirmedPhone.replace(/\D/g, '').length < 10"
              >
                Salvar
              </button>
              <button
                v-if="!phoneEditing"
                @click="confirmPhone"
                class="btn btn-primary"
                style="flex: 1;"
                :disabled="!confirmedPhone || confirmedPhone.replace(/\D/g, '').length < 10"
              >
                Sim, está correto
              </button>
            </div>
          </div>

          <div v-else class="alert alert-success mb-0">
            <strong>Telefone confirmado:</strong> {{ confirmedPhone }}
            <button @click="resetPhoneConfirmation" class="btn btn-sm btn-link float-right p-0">
              Alterar
            </button>
          </div>
        </div>
      </div>

      <template slot="footer">
        <button @click="cancelConfirmation" class="btn btn-outline mr-2">Cancelar</button>
        <button
          @click="confirmDriverIndication"
          class="btn btn-primary"
          :disabled="!phoneConfirmed"
        >
          Confirmar Indicação
        </button>
      </template>
    </Modal>

    <!-- Driver Form Modal -->
    <Modal v-model="showDriverFormModal" width="md">
      <span slot="header">Cadastrar Motorista</span>
      <DriverForm
        v-if="showDriverFormModal"
        :driver="null"
        @cancel="closeDriverForm"
        @saved="handleDriverCreated"
      />
    </Modal>

    <!-- Modal selected columns -->
    <Modal v-model="showSelectColumns" width="lg">
      <div slot="header">
        Mostrar colunas
      </div>

      <div>
        <p>Selecione as colunas que deseja visualizar na tabela de Indicação de Condutores:</p>
      </div>

      <table class="columnsSelector table table-borderless table-border table-align">
        <thead>
          <tr>
            <th></th>
            <th>Nome da Coluna</th>
            <th>Descrição</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="column in sortedTableColumns" :key="column.key">
            <td>
              <input
                  type="checkbox"
                  :id="column.key"
                  v-model="column.checked"
                  @change="updateColumnsInLocalStorage"
                  class="cursor-pointer"
              />
            </td>
            <td>{{ column.label }}</td>
            <td>{{ column.description }}</td>
          </tr>
        </tbody>
      </table>
    </Modal>

    <!-- Modal de confirmação de relatório -->
    <Modal v-model="showReportConfirmModal" width="md">
      <div slot="header">
        Confirmar geração de relatório
      </div>

      <div>
        <p>Deseja gerar o relatório <strong>Relatório Indicação de Condutores</strong> com <strong>{{ reportConfirmData.count }}</strong> registro(s)?</p>
        <p>Formato: <strong>xlsx</strong></p>
      </div>

      <div slot="footer">
        <button class="btn btn-outline mr-2" @click="showReportConfirmModal = false">
          Cancelar
        </button>
        <button class="btn btn-primary" @click="confirmReport">
          Confirmar
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import Formatter from '@/models/Formatter'
import GetUser from '@/mixins/GetUser'
import GetAccount from '@/mixins/GetAccount.vue'
import CheckedModels from '@/mixins/CheckedModels'
import ModelMapper from '@/mixins/ModelMapper'
import BadgeFilters from '@/components/shares/BadgeFilters'
import Select2 from '@/components/shares/Select2'
import AlertBannerRecords from '@/components/shares/AlertBannerRecords.vue'
import Pagination from '@/components/shares/specialTag/Pagination'
import Modal from '@/components/shares/specialTag/Modal'
import Dropdown from '@/components/shares/specialTag/Dropdown'
import DriverIndicationTable from '@/components/driver-indication/Table.vue'
import DriverSelectionModal from '@/components/driver-indication/DriverSelectionModal.vue'
import BatchReviewModal from '@/components/driver-indication/BatchReviewModal.vue'
import SingleDriverBatchModal from '@/components/driver-indication/SingleDriverBatchModal.vue'
import DeclineIndicationModal from '@/components/driver-indication/DeclineIndicationModal.vue'
import DriverForm from '@/components/drivers/FormDriver.vue'
import { SearchIcon, ChevronDownIcon } from 'vue-feather-icons'
import { Filter } from '@/models/filter'
import { IssuingAuthority } from '@/models/IssuingAuthority'
import Sorter from '@/models/Sorter'
import DriverIndicationStatuses from '@/mixins/DriverIndicationStatuses'
import { alreadyIndicated, declineBlockReason } from '@/utils/driverIndicationStatus'
import handleSearchable from '@/models/handleSearchable'

const keyOrderby = 'driver-indication-orderby'
const keyTemplateColumns = 'driverIndicationTemplateColumns'
const fiveYearsInMinutes = 2628000

export default {
  components: {
    AlertBannerRecords,
    BadgeFilters,
    Select2,
    Pagination,
    Modal,
    Dropdown,
    DriverIndicationTable,
    DriverSelectionModal,
    BatchReviewModal,
    SingleDriverBatchModal,
    DeclineIndicationModal,
    DriverForm,
    SearchIcon,
    ChevronDownIcon
  },
  superMethods: {
    buildParams: ModelMapper.methods.buildParams
  },
  computed: {
    badgesFilters() {
      return [
        this.filterCnpjs,
        this.filterDrivers,
        this.filterIssuingAuthority,
        this.filterClass,
        this.filterStatus,
        this.filterProtocol,
        this.filterStartDate,
        this.filterEndDate
      ].filter(Boolean)
    },
    selectedTickets() {
      return this.tickets.filter(ticket => ticket.checked)
    },
    someTicketSelected() {
      return this.checkAll || this.tickets.some(ticket => ticket.checked)
    },
    tickets() {
      return this.getModels || []
    },
    selectedColumns() {
      return this.tableColumns.filter(column => column.checked)
    },
    sortedTableColumns() {
      return Sorter.sortBy(this.tableColumns, 'label')
    },
    ticketsForConfirmation() {
      return this.currentBatchTickets.length > 0 ? this.currentBatchTickets : this.selectedTickets
    },
    onlyNoCrossingSelected() {
      const ticketsToCheck = this.ticketsForConfirmation.length > 0 ? this.ticketsForConfirmation : this.selectedTickets
      if (ticketsToCheck.length === 0) return false
      return ticketsToCheck.every(indication => {
        return !indication.driver
      })
    },
    indicationButtonText() {
      const count = this.selectedTickets.length
      if (this.onlyNoCrossingSelected) return `Cruzar e indicar condutor (${count})`
      return `Indicar condutor (${count})`
    },
    indicationButtonDisabled() {
      if (this.accountIsClosed) return true

      const isNoCrossing = (indication) => {
        const cpf = indication.ticket?.cpf
        const motorista = indication.driver?.name
        const categoria = indication.driver?.category
        return [cpf, motorista, categoria].every(val => !val || val === 'Não realizado cruzamento')
      }

      const hasAlreadyIndicated = this.selectedTickets.some(
        indication => alreadyIndicated(indication, this.findStatus(indication))
      )

      if (hasAlreadyIndicated) return true

      const hasNoCrossing = this.selectedTickets.some(isNoCrossing)
      const hasCrossed = this.selectedTickets.some(i => !isNoCrossing(i))

      return hasNoCrossing && hasCrossed
    },
    canDeclineIndication() {
      return this.$canNew('driver-indication', 'decline_indication')
    },
    declineCandidates() {
      return this.selectedTickets.map(indication => ({
        indication,
        reason: declineBlockReason(this.findStatus(indication), indication.ticket?.indicationDeadline)
      }))
    },
    declinableTickets() {
      return this.declineCandidates.filter(item => !item.reason).map(item => item.indication)
    },
    declineIgnoredTickets() {
      return this.declineCandidates.filter(item => item.reason)
    }
  },
  created() {
    const order = this.$ls.get(keyOrderby)
    this.order = order || ['created_at', 'desc']
    this.methods = ['driver', 'driver_indication_status']
    this.query.periodType = 'updated_at'

    this.defaultModelSetting.updateQueryURL = true

    this.asyncTemplateSelect({
      byURL: true,
      pagination: true,
      finish: () => {
        this.loadFilters()
      }
    })

    this.initializersTableColumns()
  },
  data: () => ({
    confirmedPhone: '',
    phoneConfirmed: false,
    phoneEditing: false,
    filterCnpjs: null,
    filterDrivers: null,
    filterIssuingAuthority: null,
    filterClass: null,
    filterEndDate: new Filter({
      key: 'endDate',
      label: 'Data final',
      type: 'date'
    }),
    filterStartDate: new Filter({
      key: 'startDate',
      label: 'Data inicial',
      type: 'date'
    }),
    filterStatus: null,
    filterProtocol: new Filter({
      key: 'indicationProtocol',
      label: 'Protocolo de encaminhamento',
      type: 'text'
    }),
    modelPathPrefix: 'maestro',
    nameModel: 'DriverIndication',
    selectedDriver: null,
    showDriverConfirmationModal: false,
    showDriverModal: false,
    showDriverFormModal: false,
    showBatchReviewModal: false,
    showSingleDriverBatchModal: false,
    showDeclineModal: false,
    cameFromDriverModal: false,
    singleDriverBatchData: {
      driver: null,
      tickets: []
    },
    currentBatchTickets: [],
    batchGroups: [],
    currentBatchIndex: 0,
    showFilter: false,
    showSelectColumns: false,
    showReportConfirmModal: false,
    reportConfirmData: {},
    tableColumns: [
      { key: 'ticket.ait', label: 'AIT', checked: true, description: 'Número do auto de infração' },
      { key: 'ticket.occurredAt', label: 'Data da Infração', type: 'datetime', checked: true, description: 'Data da infração' },
      { key: 'ticket.plate', label: 'Placa', checked: true, description: 'Placa do veículo' },
      { key: 'ticket.renavam', label: 'Renavam', checked: true, description: 'Número do Renavam (Registro Nacional de Veículos Automotores)' },
      { key: 'ticket.cnpj', label: 'CNPJ', type: 'cnpj', checked: false, description: 'CNPJ do proprietário do veículo' },
      { key: 'ticket.indicationDeadline', label: 'Prazo de Indicação', type: 'date', checked: true, description: 'Prazo limite de indicação do condutor' },
      { key: 'ticket.cpf', label: 'CPF', type: 'cpf', checked: true, description: 'CPF do condutor' },
      { key: 'driver.name', label: 'Motorista', checked: true, description: 'Nome do motorista' },
      { key: 'driver.category', label: 'Categoria', checked: true, description: 'Categoria do motorista' },
      { key: 'ticket.points', label: 'Pontuação', checked: false, description: 'Pontuação da infração' },
      { key: 'ticket.ticketClass', label: 'Classe', checked: true, description: 'Classe da multa' },
      { key: 'ticket.ticketSubclass', label: 'Subclasse', checked: false, description: 'Subclasse da multa' },
      { key: 'ticket.cityCode', label: 'Cidade', type: 'cityCode', checked: false, description: 'Cidade da infração' },
      { key: 'ticket.uf', label: 'UF', checked: false, description: 'Unidade federativa' },
      { key: 'ticket.issuingAuthority', label: 'Órgão Autuador', filter: 'authority', checked: true, description: 'Órgão autuador' },
      { key: 'ticket.valueCents', label: 'Valor', type: 'currency', checked: true, description: 'Valor da infração' },
      { key: 'driver_indication_status_id', label: 'Status', checked: true, description: 'Status da indicação' },
      { key: 'protocol', label: 'Protocolo de encaminhamento para indicação', checked: true, description: 'Número do protocolo de encaminhamento' },
      { key: 'createdAt', label: 'Criado Em', type: 'datetime', checked: true, description: 'Data e hora que a multa foi inserida no módulo Indicação de Condutores' },
      { key: 'updatedAt', label: 'Atualizado Em', type: 'datetime', checked: true, description: 'Data e hora em que a multa foi atualizada' }
    ],
    vehiclesMessage: 'Ainda não existem veículos cadastrados no sistema. <a href="/definitions?tab=vehicles">Clique aqui</a> para ir para o cadastro de veículos.'
  }),
  filters: {
    cpf(value) {
      if (!value) return ''
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
  },
  methods: {
    buildParams(pagination) {
      if (this.query.generic)
        this.query.generic = handleSearchable(this.query.generic)

      const params = { ...this.$options.superMethods.buildParams.call(this, pagination) }

      if (!params.q) params.q = {}

      const statusFilter = params.q.driverIndicationStatusId || params.q.driver_indication_status_id
      
      if (statusFilter && statusFilter.length > 0 && this.driverIndicationStatusMap) {
        const statusValues = Array.isArray(statusFilter) ? statusFilter : [statusFilter]
        
        const allIds = statusValues.flatMap(statusValue => 
          this.driverIndicationStatusMap[statusValue] || []
        )
        
        if (allIds.length > 0) {
          params.q.driver_indication_status_id = allIds
          delete params.q.driverIndicationStatusId
        }
      }

      return params
    },
    updateOrder(order) {
      this.find()
      this.$ls.set(keyOrderby, order, { minutes: fiveYearsInMinutes })
    },
    initializersTableColumns() {
      const keys = this.$ls.get(keyTemplateColumns)
      if (!keys)
        return

      this.tableColumns.forEach(column => column.checked = keys.includes(column.key))
    },
    updateColumnsInLocalStorage() {
      const selectedKeys = [...new Set(this.tableColumns.filter(e => e.checked).map(e => e.key))]
      this.$ls.set(keyTemplateColumns, selectedKeys, { minutes: fiveYearsInMinutes })
    },
    changeCheckAllRow(value) {
      this.changeCheck({ checkAllRow: value, sources: this.tickets })
    },
    handleSelectAll() {
      this.changeCheck({ checkAll: !this.checkAll, sources: this.tickets })
    },
    changeCheckRow() {
      this.changeCheck({ sources: this.tickets })
    },
    find() {
      this.asyncTemplateSelect()
    },
    async loadFilters() {
      const methods = [
        this.loadCompanies,
        this.loadDrivers,
        this.loadIssuingAuthorities,
        this.loadClasses,
        this.loadStatusFilter
      ]

      for (const method of methods) {
        await method()
      }
    },
    loadCompanies() {
      const params = {
        q: {
          type: ['parent', 'subsidiary'],
        },
        per_page: 10000,
        meta: false
      }

      return this.axios.get('/companies', { params })
        .then(response => response.data)
        .then((companies) => this.buildFilterCnpj(companies))
        .catch(() => {
          this.filterCnpjs = null
        })
    },
    buildFilterCnpj(companies) {
      const cnpjs = companies.map(company => [
        company.name + ' - ' + Formatter.cnpj(company.cnpj),
        company.cnpj
      ])

      this.filterCnpjs = new Filter({
        key: 'cnpj',
        label: 'Empresa',
        table: cnpjs
      })
    },
    buildFilterStatus(driverIndicationStatuses) {
      const sortedStatuses = driverIndicationStatuses.sort((a, b) => a.id - b.id)
      
      this.driverIndicationStatusMap = {}
      sortedStatuses.forEach(status => {
        const friendly_name = status.friendly_name || status.name
        if (!this.driverIndicationStatusMap[friendly_name]) {
          this.driverIndicationStatusMap[friendly_name] = []
        }
        this.driverIndicationStatusMap[friendly_name].push(status.id)
      })

      const uniqueStatuses = Object.keys(this.driverIndicationStatusMap)
        .map(friendlyName => [friendlyName, friendlyName])
      
      this.filterStatus = new Filter({
        key: 'driverIndicationStatusId',
        label: 'Status',
        table: uniqueStatuses
      })
    },
    loadIssuingAuthorities() {
      return new Promise((resolve) => {
        const uniqueAuthorities = [...new Set(this.tickets.map(t => t.ticket?.issuingAuthority || t.issuingAuthority).filter(Boolean))]

        if (uniqueAuthorities.length === 0) {
          this.filterIssuingAuthority = null
          resolve()
          return
        }

        this.filterIssuingAuthority = new Filter({
          key: 'issuingAuthority',
          label: 'Órgão Autuador',
          table: uniqueAuthorities.map(code => [IssuingAuthority.label(code) || code, code])
        })

        resolve()
      })
    },
    loadStatusFilter() {
      return this.loadDriverIndicationStatuses().then(this.buildFilterStatus)
    },
    loadDrivers() {
      return this.axios.get('/maestro/drivers?meta=false&per_page=10000')
        .then(response => {
          const drivers = response.data

          const driverOptions = [
            ['Não realizado cruzamento', null],
            ['Motorista excluído', 'Motorista excluído']
          ]

          drivers.forEach(driver => {
            const cpfFormatted = driver.cpf ? driver.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : ''
            driverOptions.push([
              `${driver.name} - ${cpfFormatted}`,
              driver.cpf
            ])
          })

          const specialOptions = driverOptions.splice(0, 2)
          driverOptions.sort((a, b) => a[0].localeCompare(b[0]))
          driverOptions.unshift(...specialOptions)

          this.filterDrivers = new Filter({
            key: 'cpf',
            label: 'Motorista',
            table: driverOptions
          })
        })
        .catch(() => {
          this.filterDrivers = null
        })
    },
    loadClasses() {
      return this.axios.get('maestro/tickets/fetch_column/ticket_class')
        .then(response => {
          const classes = response.data.filter(d => d && d !== 'NIC')

          if (classes.length === 0) {
            this.filterClass = null
            return
          }

          this.filterClass = new Filter({
            key: 'ticketClass',
            label: 'Classe',
            table: classes.map(className => [className, className])
          })
        })
        .catch(() => {
          this.filterClass = null
        })
    },
    indicateDriver() {
      if (this.selectedTickets.length === 0) {
        this.$notify.addError('Selecione pelo menos uma multa para indicar o condutor')
        return
      }

      const uniqueCpfs = [...new Set(this.selectedTickets.map(t => t.ticket?.cpf).filter(Boolean))]

      if (uniqueCpfs.length === 1) {
        const driver = this.selectedTickets[0].driver

        if (driver) {
          this.showConfirmationModal(driver)
          return
        }
      }

      if (uniqueCpfs.length > 1) {
        this.showBatchReviewModal = true
      } else {
        this.currentBatchTickets = this.selectedTickets
        this.showDriverModal = true
      }
    },
    openDriverForm() {
      this.showDriverFormModal = true
      this.showDriverModal = false
    },
    closeDriverForm() {
      this.showDriverFormModal = false
      this.showDriverModal = true
    },
    handleDriverCreated() {
      this.showDriverFormModal = false

      if (this.$refs.driverSelectionModal) {
        this.$refs.driverSelectionModal.reloadDrivers()
      }

      this.loadDrivers()
    },
    processBatchIndication(selectedGroups) {
      this.showBatchReviewModal = false

      this.batchGroups = selectedGroups
      this.currentBatchIndex = 0

      this.processNextBatchGroup()
    },
    processNextBatchGroup() {
      if (this.currentBatchIndex >= this.batchGroups.length) {
        this.$notify.add('Indicações processadas com sucesso!')
        this.batchGroups = []
        this.currentBatchIndex = 0
        this.changeCheck({ checkAll: false, sources: this.tickets })
        return
      }

      const currentGroup = this.batchGroups[this.currentBatchIndex]

      this.currentBatchTickets = currentGroup.tickets

      this.showConfirmationModal(currentGroup.driver)
    },
    handleDriverSelection(driver) {
      this.cameFromDriverModal = true
      if (this.currentBatchTickets.length > 1) {
        this.singleDriverBatchData.driver = driver
        this.singleDriverBatchData.tickets = this.currentBatchTickets
        this.showDriverModal = false
        this.showSingleDriverBatchModal = true
      } else {
        this.showConfirmationModal(driver)
      }
    },
    confirmSingleDriverBatch(selectedTickets) {
      this.showSingleDriverBatchModal = false

      this.currentBatchTickets = selectedTickets

      this.showConfirmationModal(this.singleDriverBatchData.driver)
    },
    cancelSingleDriverBatch() {
      this.showSingleDriverBatchModal = false

      this.singleDriverBatchData.driver = null
      this.singleDriverBatchData.tickets = []

      this.showDriverModal = true
    },
    showConfirmationModal(driver) {
      this.selectedDriver = driver
      this.confirmedPhone = driver.phone ? this.formatPhoneDisplay(driver.phone) : ''
      this.phoneConfirmed = false
      this.phoneEditing = false
      this.showDriverModal = false
      this.showDriverConfirmationModal = true
    },
    cancelConfirmation() {
      this.showDriverConfirmationModal = false
      this.selectedDriver = null
      this.confirmedPhone = ''
      this.phoneConfirmed = false
      this.phoneEditing = false

      if (this.batchGroups.length > 0) {
        this.batchGroups = []
        this.currentBatchIndex = 0
        this.currentBatchTickets = []
      } else if (this.cameFromDriverModal) {
        this.showDriverModal = true
      }
      this.cameFromDriverModal = false
    },
    enablePhoneEditing() {
      this.phoneEditing = true
    },
    savePhoneEdit() {
      if (this.confirmedPhone && this.confirmedPhone.replace(/\D/g, '').length >= 10) {
        const phoneClean = this.confirmedPhone.replace(/\D/g, '')

        if (!this.selectedDriver.id) {
          this.selectedDriver.phone = phoneClean
          this.phoneEditing = false
          this.phoneConfirmed = false
          return
        }

        this.axios.get('/drivers', { params: { q: { generic: `cpf=${this.selectedDriver.cpf}` }, meta: false, per_page: 1 } })
          .then(response => {
            const platformDriver = response.data.find(d => d.cpf === this.selectedDriver.cpf)
            if (!platformDriver) {
              this.$notify.addError('Motorista não encontrado')
              return false
            }
            return this.axios.put(`drivers/${platformDriver.id}`, {
              driver: { phone: phoneClean }
            }).then(() => true)
          })
          .then(success => {
            if (success) {
              this.selectedDriver.phone = phoneClean
              this.$notify.add('Telefone atualizado com sucesso!')
            }
          })
          .catch(e => this.$notify.addError(e))
          .finally(() => {
            this.phoneEditing = false
            this.phoneConfirmed = false
          })
      }
    },
    confirmPhone() {
      if (this.confirmedPhone && this.confirmedPhone.replace(/\D/g, '').length >= 10) {
        this.phoneConfirmed = true
        this.phoneEditing = false
      }
    },
    resetPhoneConfirmation() {
      this.phoneConfirmed = false
      this.phoneEditing = true
    },
    confirmDriverIndication() {
      if (!this.selectedDriver || !this.phoneConfirmed) return

      const indications = [...this.ticketsForConfirmation]
      const driver = { ...this.selectedDriver }
      const needsCrossing = this.onlyNoCrossingSelected

      this.showDriverConfirmationModal = false
      this.selectedDriver = null
      this.confirmedPhone = ''
      this.phoneConfirmed = false
      this.phoneEditing = false
      this.currentBatchTickets = []
      this.cameFromDriverModal = false

      this.openLoad()
      this.processIndicationsSequentially(indications, driver, needsCrossing, 0, 0, 0)
    },
    processIndicationsSequentially(indications, driver, needsCrossing, index, successCount, errorCount) {
      if (index >= indications.length) {
        this.closeLoad()

        if (successCount > 0) {
          this.$notify.add(`Indicação concluída! ${successCount} de ${indications.length} indicação(ões) processada(s) com sucesso.`)
        }
        if (errorCount > 0) {
          this.$notify.addError(`${errorCount} indicação(ões) apresentaram erro.`)
        }

        if (this.batchGroups.length > 0) {
          this.currentBatchIndex++
          if (this.currentBatchIndex < this.batchGroups.length) {
            this.processNextBatchGroup()
          } else {
            this.batchGroups = []
            this.currentBatchIndex = 0
          }
        }

        this.find()
        this.changeCheck({ checkAll: false, sources: this.tickets })
        return
      }

      const indication = indications[index]
      const ticketId = indication.ticket?.id || indication.ticketId
      const indicationId = indication.id
      const renavam = indication.ticket?.renavam

      const processIndication = () => {
        if (needsCrossing) {
          return this.axios.put(`maestro/tickets/${ticketId}/manual_crossing`, {
            cpf: driver.cpf
          }).then(() => {
            return this.axios.put(`maestro/driver_indications/${indicationId}/start_indication`, {
              renavam
            })
          })
        } else {
          return this.axios.put(`maestro/driver_indications/${indicationId}/start_indication`, {
            renavam
          })
        }
      }

      processIndication()
        .then(() => {
          this.processIndicationsSequentially(indications, driver, needsCrossing, index + 1, successCount + 1, errorCount)
        })
        .catch(e => {
          this.$notify.addError(e)
          this.processIndicationsSequentially(indications, driver, needsCrossing, index + 1, successCount, errorCount + 1)
        })
    },
    formatConfirmPhone() {
      let value = this.confirmedPhone.replace(/\D/g, '')
      if (value.length > 11) value = value.substring(0, 11)

      if (value.length > 10) {
        value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      } else if (value.length > 6) {
        value = value.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
      } else if (value.length > 2) {
        value = value.replace(/(\d{2})(\d{0,5})/, '($1) $2')
      } else if (value.length > 0) {
        value = value.replace(/(\d{0,2})/, '($1')
      }

      this.confirmedPhone = value
    },
    formatPhoneDisplay(phone) {
      if (!phone) return ''
      const phoneClean = phone.replace(/\D/g, '')

      if (phoneClean.length === 11) {
        return phoneClean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      } else if (phoneClean.length === 10) {
        return phoneClean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      }

      return phone
    },
    getStatusName(indication) {
      if (!indication.driver_indication_status_id || this.driverIndicationStatuses.length === 0) return '-'

      return this.findStatus(indication)?.friendly_name || indication.driver_indication_status_id
    },
    handleDeclineFinished({ succeeded }) {
      if (succeeded.length === 0) return

      this.changeCheck({ checkAll: false, sources: this.tickets })
      this.find()
    },
    openReportConfirm() {
      if (!this.someTicketSelected) {
        this.$notify.addError('Selecione pelo menos um item')
        return
      }

      let count = null
      let params = null

      if (this.checkAll) {
        params = this.buildParams()
        count = this.meta.totalCount
      } else {
        const ids = this.tickets.filter(c => c.checked).map(c => c.id)
        params = { q: { id: ids } }
        count = ids.length
      }

      this.reportConfirmData = { count, params }
      this.showReportConfirmModal = true
    },
    confirmReport() {
      const { params } = this.reportConfirmData
      this.showReportConfirmModal = false
      this.openLoad()

      this.axios.get('maestro/driver_indications/report', { params })
        .then(response => {
          if (response.data)
            this.$parse.downloadByLink(response.data)
          else if (response.meta && response.meta.notice)
            this.$notify.add(response.meta.notice)
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => {
          this.closeLoad()
          this.changeCheck({ checkAll: false, sources: this.tickets })
        })
    }
  },
  mixins: [GetUser, GetAccount, CheckedModels, ModelMapper, DriverIndicationStatuses],
  name: 'DriverIndicationIndex'
}
</script>

<style scoped>
.icon-within {
  position: absolute;
  right: 25px;
  top: 8px;
  cursor: pointer;
}

._disabled {
  opacity: 0.5;
  cursor: default;
}

/* Info boxes */
.info-box {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.info-line {
  line-height: 1.4;
  color: var(--primary-text-color);
}

.info-title {
  color: var(--primary-text-color);
}

.tickets-scroll {
  max-height: 120px;
  overflow-y: auto;
}

.ticket-detail {
  color: var(--terciary-text-color);
}

/* Phone warning section */
.alert-warning {
  background-color: var(--alert-warning-bg) !important;
  border-color: var(--alert-warning-border) !important;
  color: var(--alert-warning-text) !important;
}

.alert-warning p,
.alert-warning strong {
  color: var(--alert-warning-text) !important;
}

.alert-success {
  background-color: #d4edda !important;
  border-color: #c3e6cb !important;
  color: #155724 !important;
}

.alert-success strong {
  color: #155724 !important;
}

[data-theme="dark"] .alert-success {
  background-color: #1e3a2f !important;
  border-color: #2a5a45 !important;
  color: #75d4a0 !important;
}

[data-theme="dark"] .alert-success strong {
  color: #75d4a0 !important;
}

/* Badge info */
.badge-info {
  background-color: #17a2b8;
  color: white;
}

[data-theme="dark"] .badge-info {
  background-color: #1a3a4a;
  color: #7ecfe6;
}
</style>
